import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { StepProps, Steps } from 'antd';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalApi } from '@Apis';
import { deleteCookie, getProvisionCookie } from '../../../../../utils/common';
import CSpinner from '../../../../../components/CSpinner';
import ErrorAlert from '../../../../../components/Common/ErrorAlert';
import { StepIcon } from '../../../../Dashboard/ProvisionModal/share';

interface Props {
  onModalClose: (needRefresh: boolean) => void;
}

enum ProvisionStatusEnum {
  NotStarted = 0,
  Done = 1,
}

const AddModal = ({ onModalClose }: Props) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [provisionStatus, setProvisionStatus] = useState<ProvisionStatusEnum>(
    ProvisionStatusEnum.NotStarted
  );
  const [authUrl, setAuthURL] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const intervalIdRef = useRef<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const onClose = () => {
    if (provisionStatus === ProvisionStatusEnum.NotStarted) {
      onModalClose(false);
    } else {
      onModalClose(true);
    }
  };

  const onGrantPermission = () => {
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    // reportPendo('Provision.GrantPermission.Click', {
    //   orgId,
    //   service: 'exchangeInline',
    // });
    setLoading(true);
    window.open(authUrl, '_blank');
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
    intervalIdRef.current = setInterval(async () => {
      const provisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_notification'
      );
      if (provisionInfo) {
        clearInterval(intervalIdRef.current);
        deleteCookie('provision_status_');
        //{"code": "123"}
        const { error, code } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res =
              await GlobalApi.saveNotificationMailboxesProvisionInfo(code);
            if (res.data?.status === 'failure') {
              setErrorMsg('common:common.apiException.internalError');
            } else {
              setProvisionStatus(ProvisionStatusEnum.Done);
            }
          } catch {
            setErrorMsg(t('common:common.apiException.internalError'));
          }
        } else {
          setErrorMsg(
            t('provision:provision.service.graphNotification.error.deniedGrant')
          );
        }
        setLoading(false);
      }
    }, 1000);
  };

  const stepItems: StepProps[] = [
    {
      icon: <StepIcon checked={provisionStatus === ProvisionStatusEnum.Done} />,
      title: (
        <Text
          fontSize="16px"
          lineHeight="22px"
          fontWeight="400"
          color={colorStyle.color.primary}
        >
          {t('globalSetting:globalSetting.sendingMailboxes.modal.add.grant')}
        </Text>
      ),
      description: (
        <Button
          mt="12px"
          onClick={onGrantPermission}
          variant="secondary"
          borderRadius="2rem"
        >
          {t('provision:provision.button.grantPermission')}
        </Button>
      ),
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        const authInfo = (
          await GlobalApi.getNotificationMailboxesProvisionAuthInfo()
        ).data;
        setAuthURL(authInfo[0].authenticationUrl || '');
      } catch {
        console.error('getNotificationMailboxesProvisionAuthInfo error');
      }
    })();
  }, []);

  if (authUrl === '') {
    return <></>;
  }

  return (
    <>
      <Modal isClosable isOpen={true} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('globalSetting:globalSetting.sendingMailboxes.modal.add.title')}
          </ModalHeader>
          <ModalBody>
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column">
              <Steps
                direction="vertical"
                current={provisionStatus + 1}
                items={stepItems}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              minWidth="20x"
              variant="primary"
              onClick={onClose}
              disabled={provisionStatus !== ProvisionStatusEnum.Done}
            >
              {t('common:common.label.done')}
            </Button>
            <Space width="2x" />
            <Button minWidth="20x" onClick={onClose}>
              {t('common:common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {loading && <CSpinner />}
    </>
  );
};

export default memo(AddModal);
